import moment from 'moment';

// Eventos de e-mail (email_logs.event e o histórico de cada envio).
var EMAIL_EVENTS = {
  draft: { title: 'E-mail preparado', color: '#8c8c8c', icon: 'edit' },
  duplicated: { title: 'Duplicado, não enviado', color: '#fa8c16', icon: 'copy' },
  pendent: { title: 'Envio solicitado', color: '#722ed1', icon: 'clock-circle' },
  accepted: { title: 'Aceito pelo provedor', color: '#1890ff', icon: 'cloud-upload' },
  sent: { title: 'E-mail enviado', color: '#0071c0', icon: 'mail' },
  delivered: { title: 'E-mail entregue', color: '#0071c0', icon: 'mail' },
  opened: { title: 'E-mail aberto', color: '#03b14e', icon: 'eye' },
  clicked: { title: 'Link clicado', color: '#03b14e', icon: 'link' },
  failed: { title: 'Falha no envio', color: '#f5222d', icon: 'close-circle', isFailure: true },
  bounced: { title: 'E-mail devolvido', color: '#f5222d', icon: 'rollback', isFailure: true },
  complained: { title: 'Marcado como spam', color: '#fa8c16', icon: 'warning', isFailure: true },
  unsubscribed: { title: 'Descadastrado', color: '#434343', icon: 'stop', isFailure: true },
  blacklist: { title: 'Bloqueado (blacklist)', color: '#000000', icon: 'stop', isFailure: true },
};

// Marcos do próprio documento.
var DOCUMENT_EVENTS = {
  task_executed: { title: 'Tarefa executada', color: '#13c2c2', icon: 'check-square' },
  created: { title: 'Documento cadastrado', color: '#1890ff', icon: 'file-add' },
  revised: { title: 'Documento revisado', color: '#2f54eb', icon: 'audit' },
  protocol_generated: { title: 'Protocolo gerado', color: '#595959', icon: 'file-protect' },
  panel_sent: { title: 'Disponibilizado no painel do cliente', color: '#1890ff', icon: 'desktop' },
  whatsapp_sent: { title: 'Enviado por WhatsApp', color: '#25d366', icon: 'message' },
  impersonated: { title: 'Aberto como cliente pelo escritório', color: '#722ed1', icon: 'user' },
  viewed: { title: 'Baixado pelo cliente', color: '#03b14e', icon: 'download' },
  archived: { title: 'Documento arquivado', color: '#f5222d', icon: 'inbox' },
  deleted: { title: 'Documento excluído', color: '#f5222d', icon: 'delete' },
};

var FALLBACK = { title: 'Evento', color: '#8c8c8c', icon: 'info-circle' };

export var getTimelineEventMeta = function(event) {
  if (!event) return FALLBACK;
  if (event.type === 'email') {
    if (event.event === 'clicked' && event.robot) {
      return { title: 'Clique automático (antivírus do destinatário)', color: '#8c8c8c', icon: 'robot' };
    }
    return EMAIL_EVENTS[event.event] || Object.assign({}, FALLBACK, { title: 'E-mail: ' + event.event });
  }
  return DOCUMENT_EVENTS[event.type] || FALLBACK;
};

export var getEmailEventMeta = function(event) {
  return EMAIL_EVENTS[event] || Object.assign({}, FALLBACK, { title: event || 'Sem status' });
};

var GATEWAY_LABELS = { cloudflare: 'Cloudflare', resend: 'Resend', mailgun: 'Mailgun', mailersend: 'MailerSend' };

export var getGatewayLabel = function(gateway) {
  return gateway ? (GATEWAY_LABELS[gateway] || gateway) : null;
};

var REACHED = ['sent', 'delivered', 'opened', 'clicked'];

// Um resumo por destinatário: o estado mais recente de cada e-mail.
export var summarizeEmails = function(emails) {
  var list = emails || [];
  var recipients = {};
  list.forEach(function(email) {
    var key = (email.email_to || '').toLowerCase();
    var previous = recipients[key];
    if (!previous || (email.created_at || '') > (previous.created_at || '')) {
      recipients[key] = email;
    }
  });
  var latest = Object.keys(recipients).map(function(key) { return recipients[key]; });

  // O histórico manda (ignora clique de antivírus); o status atual só conta
  // quando não há histórico.
  var hasEvent = function(email, names) {
    var history = email.history || [];
    if (history.length === 0) return names.indexOf(email.event) !== -1;
    return history.some(function(entry) {
      return names.indexOf(entry.event) !== -1 && !(entry.event === 'clicked' && entry.robot);
    });
  };

  return {
    total: list.length,
    recipients: latest.length,
    delivered: latest.filter(function(email) { return hasEvent(email, REACHED); }).length,
    opened: latest.filter(function(email) { return hasEvent(email, ['opened', 'clicked']); }).length,
    failed: latest.filter(function(email) {
      var meta = EMAIL_EVENTS[email.event];
      return !!(meta && meta.isFailure);
    }).length,
  };
};

// Filtros da coluna da linha do tempo: tipo (todos/e-mails/documento) e destinatário.
export var filterTimelineEvents = function(events, kind, recipient) {
  return (events || []).filter(function(event) {
    if (kind === 'emails' && event.type !== 'email') return false;
    if (kind === 'document' && event.type === 'email') return false;
    if (recipient && event.type === 'email' && (event.email_to || '').toLowerCase() !== recipient.toLowerCase()) return false;
    if (recipient && event.type !== 'email' && kind === 'emails') return false;
    return true;
  });
};

// Agrupa por dia, do mais recente para o mais antigo (ou ao contrário).
export var groupEventsByDay = function(events, newestFirst) {
  var sorted = (events || []).slice().sort(function(a, b) {
    return newestFirst ? (a.date < b.date ? 1 : -1) : (a.date < b.date ? -1 : 1);
  });
  var groups = [];
  sorted.forEach(function(event) {
    var day = moment(event.date).format('YYYY-MM-DD');
    var last = groups[groups.length - 1];
    if (!last || last.day !== day) {
      last = { day: day, events: [] };
      groups.push(last);
    }
    last.events.push(event);
  });
  return groups;
};

// A situação do documento para o cliente, igual à tag da tabela.
export var getDocumentSituation = function(doc) {
  if (!doc) return null;
  if (doc.deleted_at) return { label: 'Excluído', color: '#595959' };
  if (doc.archived_at) return { label: 'Arquivado', color: '#f5222d' };
  if (doc.viewed_at) return { label: 'Baixado', color: '#03b14e' };
  if (doc.due_at && moment(doc.due_at).isBefore(moment(), 'day')) return { label: 'Vencido', color: '#ff0a02' };
  return { label: 'Não baixado', color: '#0071c0' };
};

export var formatIdentification = function(value) {
  var digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return value || null;
};

export var formatPhone = function(value) {
  var digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return value || null;
};

var firstEmailEvent = function(events, names) {
  var found = (events || []).filter(function(event) {
    return event.type === 'email' && names.indexOf(event.event) !== -1 && !(event.event === 'clicked' && event.robot);
  }).sort(function(a, b) { return a.date < b.date ? -1 : 1; })[0];
  return found ? found.date : null;
};

// A jornada do documento até o cliente, para ver de relance onde parou.
export var getDocumentJourney = function(doc, events) {
  if (!doc) return [];
  var sentDates = [doc.email_sent_at, doc.panel_sent_at, doc.whatsapp_sent_at, doc.protocol_generated_at]
    .filter(Boolean)
    .sort();
  var steps = [
    { key: 'created', label: 'Cadastrado', date: doc.created_at, who: doc.created_by },
    { key: 'revised', label: 'Revisado', date: doc.revised_at, who: doc.revised_by },
    { key: 'sent', label: 'Enviado', date: sentDates[0] || firstEmailEvent(events, ['draft', 'pendent', 'sent', 'delivered']) },
    { key: 'delivered', label: 'Entregue', date: firstEmailEvent(events, ['sent', 'delivered', 'opened', 'clicked']) || doc.panel_sent_at || doc.whatsapp_sent_at },
    { key: 'opened', label: 'Aberto', date: firstEmailEvent(events, ['opened', 'clicked']) },
    { key: 'viewed', label: 'Baixado', date: doc.viewed_at, who: doc.viewed_by },
  ];
  var lastDone = -1;
  steps.forEach(function(step, index) { if (step.date) lastDone = index; });
  return steps.map(function(step, index) {
    return Object.assign({}, step, {
      done: !!step.date,
      // Etapa sem data antes da última concluída: foi pulada (ex.: baixou sem abrir o e-mail).
      skipped: !step.date && index < lastDone,
      current: index === lastDone + 1,
    });
  });
};

// Passos técnicos do envio: ficam escondidos até pedir "detalhes técnicos".
var TECHNICAL_EMAIL_EVENTS = ['draft', 'pendent', 'accepted'];

export var isTechnicalEvent = function(event) {
  if (!event || event.type !== 'email') return false;
  if (event.event === 'clicked' && event.robot) return true;
  return TECHNICAL_EMAIL_EVENTS.indexOf(event.event) !== -1;
};

var dayAndTime = function(date) {
  return moment(date).format('DD/MM [às] HH:mm');
};

// Uma frase que responde "o cliente recebeu? baixou?".
export var getDocumentHeadline = function(doc, emails) {
  if (!doc) return null;
  if (doc.deleted_at) return { tone: 'neutral', parts: ['Documento excluído em ' + dayAndTime(doc.deleted_at)] };
  if (doc.archived_at) {
    return { tone: 'neutral', parts: ['Arquivado em ' + dayAndTime(doc.archived_at) + (doc.archived_by && doc.archived_by.name ? ' por ' + doc.archived_by.name : '')] };
  }

  var parts = [];
  var summary = summarizeEmails(emails);
  var channels = doc.delivery_methods || [];
  var tone = 'ok';

  if (summary.recipients > 0) {
    parts.push(summary.delivered === summary.recipients
      ? (summary.recipients === 1 ? 'E-mail entregue' : 'E-mail entregue para os ' + summary.recipients + ' destinatários')
      : 'E-mail entregue para ' + summary.delivered + ' de ' + summary.recipients + ' destinatários');
    if (summary.failed > 0) {
      parts.push(summary.failed + ' com falha');
      tone = 'warn';
    }
  } else if (channels.length > 0 || doc.panel_sent_at || doc.whatsapp_sent_at) {
    parts.push('Disponibilizado ao cliente');
  } else {
    parts.push('Ainda não enviado ao cliente');
    tone = 'warn';
  }

  if (doc.viewed_at) {
    parts.push('Baixado pelo cliente em ' + dayAndTime(doc.viewed_at));
  } else if (doc.due_at && moment(doc.due_at).isBefore(moment(), 'day')) {
    parts.push('Vencido e não baixado');
    tone = 'bad';
  } else {
    parts.push('Ainda não baixado');
    if (tone === 'ok') tone = 'pending';
  }

  return { tone: tone, parts: parts };
};
